import React, { useState, useContext, useEffect, useId } from "react";
import { Box, ButtonBase, Grid, InputAdornment, TextField, Typography } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";

import { DataContext } from "../../../../context/DataContext";
import { getToken } from "../../../../api/Account";
import { addGarantiaAsync, updateGarantiaAsync } from "../../../../api/Credits";
import { SelectorFotos, useAviso } from "../../../../components/base";
import { DialogoFormulario, useEnvio } from "../../../rrhh/employees/comunes";
import PreviewModal from "../../../../components/modals/PreviewModal";
import { esValorEscribible, formularioGarantia, validarGarantia } from "./garantia";

/**
 * Alta (sin `garantia`) y edición de una garantía del cliente del crédito. Las fotos elegidas
 * reemplazan a las actuales; en la edición, sin fotos nuevas se conservan las que tiene.
 */
const DialogoGarantia = ({ creditId, garantia, onCerrar, onGuardado }) => {
  const esAlta = !garantia;
  const token = getToken();
  const id = useId();
  const { setIsLoading, setIsUnautorized } = useContext(DataContext);
  const aviso = useAviso();
  const { guardando, enviar } = useEnvio();

  // Lo que tenía al abrir: sirve para saber si hay algo que no se debe perder con un clic fuera.
  const [inicial] = useState(() => ({
    articulo: garantia?.articulo || "",
    marca: garantia?.marca || "",
    color: garantia?.color || "",
    modelo: garantia?.modelo || "",
    serie: garantia?.serie || "",
    valorEstimado: garantia?.valorEstimado ? String(garantia.valorEstimado) : "",
  }));
  const [datos, setDatos] = useState(inicial);
  const [fotos, setFotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [intentado, setIntentado] = useState(false);
  // Foto actual abierta en grande (el visor de la app, con zoom, descarga e impresión).
  const [fotoGrande, setFotoGrande] = useState("");

  const fotosActuales = (garantia?.garantiaPhotos || []).filter((f) => f.isActive !== false);
  const errores = intentado
    ? validarGarantia({ ...datos, fotos, fotosActuales: fotosActuales.length })
    : {};

  useEffect(
    () => () => {
      previews.forEach((p) => URL.revokeObjectURL(p.preview));
    },
    []
  );

  const cambiar = (campo) => (e) => setDatos((d) => ({ ...d, [campo]: e.target.value.toUpperCase() }));

  const onChangeFile = (e) => {
    const archivos = Array.from(e.target.files || []);
    setFotos(archivos);
    setPreviews(archivos.map((file) => ({ file, preview: URL.createObjectURL(file) })));
    e.target.value = "";
  };

  const quitarFoto = (indice) => {
    URL.revokeObjectURL(previews[indice].preview);
    setPreviews(previews.filter((_, i) => i !== indice));
    setFotos(fotos.filter((_, i) => i !== indice));
  };

  const hayCambios = fotos.length > 0 || Object.keys(datos).some((k) => datos[k] !== inicial[k]);

  const guardar = () =>
    enviar(async () => {
      setIntentado(true);
      const errs = validarGarantia({ ...datos, fotos, fotosActuales: fotosActuales.length });
      if (Object.keys(errs).length > 0) return;
      const formData = formularioGarantia({
        ...datos,
        fotos,
        ...(esAlta ? { creditId } : { id: garantia.id }),
      });
      setIsLoading(true);
      try {
        const result = esAlta
          ? await addGarantiaAsync(token, formData)
          : await updateGarantiaAsync(token, formData);
        setIsLoading(false);
        if (!result.statusResponse) {
          if (result.error === "Unauthorized") {
            setIsUnautorized(true);
            return;
          }
          aviso.error(result.error || "No se pudo guardar la garantía");
          return;
        }
        onGuardado(esAlta ? "Garantía agregada" : "Garantía actualizada");
      } catch (e) {
        setIsLoading(false);
        aviso.error("No se pudo conectar con el servidor. Inténtalo de nuevo.");
      }
    });

  const campo = (nombre, label, { requerido = false, ayuda = " " } = {}) => (
    <TextField
      fullWidth
      id={`${id}-${nombre}`}
      label={label}
      required={requerido}
      value={datos[nombre]}
      onChange={cambiar(nombre)}
      error={Boolean(errores[nombre])}
      helperText={errores[nombre] || ayuda}
      disabled={guardando}
      inputProps={{ autoComplete: "off" }}
    />
  );

  return (
    <DialogoFormulario
      titulo={esAlta ? "Agregar garantía" : `Editar garantía #${garantia.id}`}
      onCerrar={onCerrar}
      hayCambios={hayCambios}
      guardando={guardando}
      onEnviar={guardar}
      textoEnviar={esAlta ? "Agregar garantía" : "Guardar cambios"}
    >
      {/* rowSpacing: la ayuda bajo Marca y Modelo no debe tocar la etiqueta de la fila siguiente. */}
      <Grid container columnSpacing={2} rowSpacing={1.5} sx={{ mt: 0.5 }}>
        <Grid item xs={12} sm={6}>
          {campo("articulo", "Artículo", { requerido: true })}
        </Grid>
        <Grid item xs={12} sm={6}>
          {campo("color", "Color", { requerido: true })}
        </Grid>
        <Grid item xs={12} sm={6}>
          {campo("marca", "Marca", { ayuda: "Vacía se guarda como S/M" })}
        </Grid>
        <Grid item xs={12} sm={6}>
          {campo("modelo", "Modelo", { ayuda: "Vacío se guarda como S/M" })}
        </Grid>
        <Grid item xs={12} sm={6}>
          {campo("serie", "Serie", { ayuda: "Vacía se guarda como S/S" })}
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            id={`${id}-valor`}
            label="Valor estimado"
            value={datos.valorEstimado}
            onChange={(e) => {
              if (esValorEscribible(e.target.value)) setDatos((d) => ({ ...d, valorEstimado: e.target.value }));
            }}
            error={Boolean(errores.valorEstimado)}
            helperText={errores.valorEstimado || "En córdobas, sin centavos"}
            disabled={guardando}
            inputProps={{ inputMode: "numeric", autoComplete: "off" }}
            InputProps={{ startAdornment: <InputAdornment position="start">C$</InputAdornment> }}
          />
        </Grid>
      </Grid>

      <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
        Fotos de la garantía
      </Typography>
      {fotosActuales.length > 0 ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {fotos.length > 0
              ? "Las fotos elegidas reemplazan a las actuales al guardar."
              : "Fotos actuales: pulse una para verla en grande. Si no elige otras, se conservan."}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
            {fotosActuales.map((foto, i) => (
              <ButtonBase
                key={foto.id ?? i}
                onClick={() => setFotoGrande(foto.imageFullPath)}
                aria-label={`Ver en grande la foto ${i + 1} de la garantía`}
                sx={{
                  position: "relative",
                  borderRadius: 1,
                  overflow: "hidden",
                  "&:hover .lupa, &:focus-visible .lupa": { opacity: 1 },
                  "&:focus-visible": {
                    outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Box
                  component="img"
                  src={foto.imageFullPath}
                  alt=""
                  sx={{
                    display: "block",
                    width: 120,
                    height: 120,
                    objectFit: "cover",
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                    opacity: fotos.length > 0 ? 0.4 : 1,
                  }}
                />
                {/* Lupa: indica que la foto se abre; más visible al pasar el mouse o con el foco. */}
                <Box
                  className="lupa"
                  aria-hidden="true"
                  sx={{
                    position: "absolute",
                    right: 6,
                    bottom: 6,
                    display: "flex",
                    p: 0.5,
                    borderRadius: "50%",
                    bgcolor: "rgba(0, 0, 0, 0.6)",
                    color: "common.white",
                    opacity: 0.85,
                    transition: "opacity 150ms",
                  }}
                >
                  <ZoomInIcon fontSize="small" />
                </Box>
              </ButtonBase>
            ))}
          </Box>
        </Box>
      ) : null}
      <SelectorFotos
        previews={previews}
        onChange={onChangeFile}
        onRemove={quitarFoto}
        error={errores.fotos}
        disabled={guardando}
      />

      <PreviewModal
        openModal={Boolean(fotoGrande)}
        selectedImage={fotoGrande}
        setOpenModal={(abierto) => {
          if (!abierto) setFotoGrande("");
        }}
        setSelectedImage={setFotoGrande}
      />
    </DialogoFormulario>
  );
};

export default DialogoGarantia;
